import { useCallback, useEffect, useRef, useState } from 'react'

import { type Enum } from '@webapp/infrastructure/types/Enum'
import { ITEM_DETAILS_TIMEOUT_MS } from '../../constants'
import { type ConversationTechnicalMessage } from '../../value-objects/ConversationTechnicalMessage'
import {
  type ConversationCatalogItemComponent,
  type ConversationComponent,
  type ConversationDetailSection,
} from '../../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationTechnicalMessageActions,
} from '../../value-objects/enums'
import { type ConversationTechnicalMessageHandlers } from '../useConversationStream'

export const CatalogItemDetailsStatuses = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
} as const

export type CatalogItemDetailsStatusEnum = Enum<
  typeof CatalogItemDetailsStatuses
>

export interface CatalogItemDetailsState {
  status: CatalogItemDetailsStatusEnum
  detailSections: ConversationDetailSection[]
}

export type SendTechnicalMessage = (
  technicalMessage: ConversationTechnicalMessage,
  handlers: ConversationTechnicalMessageHandlers
) => Nullable<() => void>

export interface UseCatalogItemDetailsResult {
  detailsOf: (itemId: string) => CatalogItemDetailsState
  requestItemDetails: (itemId: string) => void
}

const IDLE_DETAILS: CatalogItemDetailsState = {
  status: CatalogItemDetailsStatuses.IDLE,
  detailSections: [],
}

const FAILED_DETAILS: CatalogItemDetailsState = {
  status: CatalogItemDetailsStatuses.ERROR,
  detailSections: [],
}

const findCatalogItem = (
  components: ConversationComponent[],
  itemId: string
): ConversationCatalogItemComponent | undefined =>
  components.find(
    (component): component is ConversationCatalogItemComponent =>
      component.type === ConversationComponentTypes.CATALOG_ITEM &&
      component.itemId === itemId
  )

export const useCatalogItemDetails = (
  sendTechnicalMessage: SendTechnicalMessage
): UseCatalogItemDetailsResult => {
  const [detailsById, setDetailsById] = useState<
    Record<string, CatalogItemDetailsState>
  >({})
  const statusByIdRef = useRef(new Map<string, CatalogItemDetailsStatusEnum>())
  const timeoutIdsRef = useRef(new Set<ReturnType<typeof setTimeout>>())

  const storeDetails = useCallback(
    (itemId: string, details: CatalogItemDetailsState) => {
      statusByIdRef.current.set(itemId, details.status)
      setDetailsById((prev) => ({ ...prev, [itemId]: details }))
    },
    []
  )

  const requestItemDetails = useCallback(
    (itemId: string) => {
      const status = statusByIdRef.current.get(itemId)
      if (
        status === CatalogItemDetailsStatuses.LOADING ||
        status === CatalogItemDetailsStatuses.READY
      ) {
        return
      }

      let isSettled = false
      let timeoutId: Nullable<ReturnType<typeof setTimeout>> = null
      const settle = (details: CatalogItemDetailsState) => {
        if (isSettled) return
        isSettled = true
        if (timeoutId !== null) {
          clearTimeout(timeoutId)
          timeoutIdsRef.current.delete(timeoutId)
        }
        storeDetails(itemId, details)
      }

      const disconnect = sendTechnicalMessage(
        {
          action: ConversationTechnicalMessageActions.GET_ITEM_DETAILS,
          params: { itemId },
        },
        {
          onComponents: (components) => {
            const item = findCatalogItem(components, itemId)
            if (!item) return
            settle({
              status: CatalogItemDetailsStatuses.READY,
              detailSections: item.detailSections,
            })
          },
          onDone: () => settle(FAILED_DETAILS),
          onError: () => settle(FAILED_DETAILS),
        }
      )
      if (!disconnect) return

      storeDetails(itemId, {
        status: CatalogItemDetailsStatuses.LOADING,
        detailSections: [],
      })
      timeoutId = setTimeout(() => {
        settle(FAILED_DETAILS)
        disconnect()
      }, ITEM_DETAILS_TIMEOUT_MS)
      timeoutIdsRef.current.add(timeoutId)
    },
    [sendTechnicalMessage, storeDetails]
  )

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current
    return () => timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId))
  }, [])

  const detailsOf = useCallback(
    (itemId: string) => detailsById[itemId] ?? IDLE_DETAILS,
    [detailsById]
  )

  return { detailsOf, requestItemDetails }
}
